import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Star } from "lucide-react";
import { createSessionReview, type SessionReview } from "@/services/sessionService";
import { useToast } from "@/components/ui/use-toast";
import { useAuth } from "@/hooks/auth";

interface ReviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  sessionId: string;
  mentorId: string;
  mentorName: string;
  onReviewSubmitted: () => void;
}

export const ReviewModal: React.FC<ReviewModalProps> = ({
  isOpen,
  onClose,
  sessionId,
  mentorId,
  mentorName,
  onReviewSubmitted
}) => {
  const [rating, setRating] = useState(0);
  const [hoveredRating, setHoveredRating] = useState(0);
  const [comment, setComment] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();
  const { user } = useAuth();

  const handleSubmit = async () => {
    if (!user) return;
    
    if (rating === 0) {
      toast({
        variant: "destructive",
        title: "Avaliação obrigatória",
        description: "Por favor, selecione uma nota de 1 a 5 estrelas."
      });
      return;
    }

    if (comment.trim().length < 10) {
      toast({
        variant: "destructive", 
        title: "Comentário muito curto",
        description: "Por favor, escreva pelo menos 10 caracteres no seu comentário."
      });
      return;
    }

    setIsSubmitting(true);
    
    try {
      console.log(`📝 [ReviewModal] Iniciando envio de avaliação para sessão:`, sessionId);
      console.log(`📝 [ReviewModal] Dados do usuário:`, user.id);
      console.log(`📝 [ReviewModal] Mentor ID:`, mentorId);
      console.log(`📝 [ReviewModal] Rating:`, rating);
      console.log(`📝 [ReviewModal] Comment length:`, comment.trim().length);
      
      const review: SessionReview = {
        session_id: sessionId,
        player_id: user.id,
        mentor_id: mentorId,
        rating: rating,
        comment: comment.trim()
      };

      console.log(`📝 [ReviewModal] Review object:`, review);

      await createSessionReview(review);
      
      toast({
        title: "Avaliação enviada!",
        description: "Sua avaliação foi registrada com sucesso."
      });
      
      onReviewSubmitted();
      onClose();
      
      // Reset form
      setRating(0);
      setComment("");
      
    } catch (error) {
      console.error("Erro ao enviar avaliação:", error);
      
      let errorMessage = "Não foi possível enviar sua avaliação. Tente novamente.";
      
      if (error instanceof Error) {
        // Usar a mensagem específica do erro
        errorMessage = error.message;
      }
      
      toast({
        variant: "destructive",
        title: "Erro ao enviar avaliação",
        description: errorMessage
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderStars = () => {
    return (
      <div className="flex items-center gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            type="button"
            className="p-1 transition-transform hover:scale-110"
            onMouseEnter={() => setHoveredRating(star)}
            onMouseLeave={() => setHoveredRating(0)}
            onClick={() => setRating(star)}
          >
            <Star
              size={32}
              className={`transition-colors ${
                star <= (hoveredRating || rating)
                  ? "fill-yellow-400 text-yellow-400"
                  : "text-gray-400"
              }`}
            />
          </button>
        ))}
      </div>
    );
  };

  return (
    <Dialog open={isOpen} onOpenChange={() => {}} modal>
      <DialogContent 
        className="bg-[#1A2233] border-[#222A35] text-white max-w-md"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className="text-xl text-center">
            Avaliar Mentoria
          </DialogTitle>
          <DialogDescription className="text-gray-400 text-center">
            Como foi sua experiência com {mentorName}?
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-6">
          {/* Rating */}
          <div className="text-center">
            <p className="text-sm text-gray-300 mb-3">
              Dê uma nota de 1 a 5 estrelas:
            </p>
            <div className="flex justify-center">
              {renderStars()}
            </div>
            {rating > 0 && (
              <p className="text-sm text-gray-400 mt-2">
                {rating === 1 && "Muito ruim"}
                {rating === 2 && "Ruim"}
                {rating === 3 && "Regular"}
                {rating === 4 && "Bom"}
                {rating === 5 && "Excelente"}
              </p>
            )}
          </div>

          {/* Comment */}
          <div>
            <p className="text-sm text-gray-300 mb-2">
              Comentário sobre a mentoria:
            </p>
            <Textarea
              placeholder="Conte como foi sua experiência, o que você aprendeu, pontos positivos, sugestões de melhoria..."
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              className="bg-[#2A3749] border-[#3A4759] text-white placeholder-gray-400 min-h-[100px] resize-none"
              maxLength={500}
            />
            <p className="text-xs text-gray-500 mt-1">
              {comment.length}/500 caracteres
            </p>
          </div>

          {/* Buttons */}
          <div className="flex gap-3 pt-4">
            <Button
              variant="outline"
              onClick={onClose}
              disabled={isSubmitting}
              className="flex-1 border-[#3A4759] text-gray-300 hover:bg-[#2A3749]"
            >
              Cancelar
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={isSubmitting || rating === 0}
              className="flex-1 bg-[#00B6F0] hover:bg-[#0096CC] text-white"
            >
              {isSubmitting ? "Enviando..." : "Enviar Avaliação"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}; 